import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import CodeQuizEditor from "@/components/storyboard/code/CodeQuizEditor";
import { CodeQuizWorkspace } from "@/components/storyboard/code/CodeQuizWorkspace";
import { CodeQuizActions } from "@/components/storyboard/quiz/CodeQuizActions";
import { codeQuizReview } from "@/components/storyboard/quiz/CodeQuizVerdict";
import { QuizChallengeHeading } from "@/components/storyboard/quiz/QuizChallengeHeading";
import { deriveDefaultLanguage } from "@/components/storyboard/code/codeUtils";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { ValidationSubmission } from "@/lib/validationSubmission";
import { useQuizCodeState } from "@/hooks/useQuizCodeState";
import type { QuizCodeFreetextEntryData } from "@/types/storyboard";
import {
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";

export interface QuizCodeFreetextProps extends HiddenVerdictProps {
  entries: QuizCodeFreetextEntryData[];
  /** Submits the learner's code for server-side (LLM) validation; also persists it. */
  onValidate: (code: string, entryIndex: number) => Promise<ValidationResult>;
  /** Saved submissions per entry index, used to re-seed the editor on reopen. */
  initialSubmissions?: Record<number, ValidationSubmission>;
  /** Called after a submission is persisted, so saved state can be refreshed. */
  onSubmitted?: () => void;
}

/**
 * Code-freetext quiz: the learner writes code from a task and submits it.
 * "Code prüfen" runs server-side (LLM) validation, which also persists the
 * answer. Each task grants three tries; no localStorage — durable state lives
 * in validation_data.
 */
const QuizCodeFreetext = ({
  entries,
  onValidate,
  initialSubmissions,
  onSubmitted,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizCodeFreetextProps) => {
  const defaultLanguage = deriveDefaultLanguage(
    entries.flatMap(
      (entry) => entry.solutions ?? entry.referenceSolutions ?? []
    )
  );

  const quiz = useQuizCodeState({
    entries,
    initialSubmissions,
    onValidate,
    onSubmitted,
    revealFeedback,
    onAdvance,
    getDefaultInput: () => "",
  });
  const {
    currentEntry,
    currentInput: currentCode,
    currentResult,
    isSettled,
    isValidating,
    handleInputChange: handleCodeChange,
  } = quiz;

  if (!entries || entries.length === 0) return null;
  if (!currentEntry) return null;

  const challenge = currentEntry.challenge ?? currentEntry.codeChallenge;

  const meta = (
    <>
      <QuizChallengeHeading challenge={challenge} />

      {currentEntry.subtasks && currentEntry.subtasks.length > 0 && (
        <div>
          <p className="font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Teilaufgaben
          </p>
          <ol className="space-y-2">
            {currentEntry.subtasks.map((subtask, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full bg-info-light text-info-foreground font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <div className="prose max-w-full text-gray-700">
                  <CourseMarkdown>{subtask}</CourseMarkdown>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </>
  );

  const editor = (
    <CodeQuizEditor
      label="Dein Code"
      value={currentCode}
      onChange={handleCodeChange}
      language={defaultLanguage}
      readOnly={isValidating || isSettled}
    />
  );

  const actions = (
    <CodeQuizActions
      quiz={quiz}
      submitLabel={
        collectsWithoutVerdict(revealFeedback)
          ? (advanceLabel ?? "Nächste Frage")
          : "Code prüfen"
      }
      canSubmit={currentCode.trim().length > 0}
    />
  );

  return (
    <CodeQuizWorkspace
      meta={meta}
      feedback={codeQuizReview(
        currentResult,
        "Nutze das Feedback und überarbeite deinen Code – er bleibt erhalten."
      )}
      editor={editor}
      actions={actions}
    />
  );
};

export default QuizCodeFreetext;
